import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Zap, Plus, Trash2, Edit2, Eye, X, Mail, Clock, Share2, CheckCircle, ShieldCheck, ShieldAlert, CalendarClock } from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, GlassSelect } from '../ui';
import {
    generateSequence, createStep, updateStep, deleteStep, reorderSteps,
    executeEmailSequence,
    Campaign, EmailSequence, EmailSequenceStep,
    apiClient,
} from '../../services/api';
import './Campaigns.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
};

const EmailPreviewModal: React.FC<{ step: EmailSequenceStep; onClose: () => void }> = ({ step, onClose }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [html, setHtml] = useState<string>('');

    useEffect(() => {
        if (!step.step_id) return;
        let cancelled = false;
        apiClient.get(`/campaigns/steps/${step.step_id}/rendered?sample=true`, {
            responseType: 'text',
            transformResponse: [(data: any) => data],
        }).then(res => {
            if (!cancelled) setHtml(res.data);
        }).catch(() => {
            if (!cancelled) setHtml('<p style="padding:20px;color:#ef4444">Preview failed to load.</p>');
        });
        return () => { cancelled = true; };
    }, [step.step_id]);

    useEffect(() => {
        if (!html || !iframeRef.current) return;
        const doc = iframeRef.current.contentDocument;
        if (doc) { doc.open(); doc.write(html); doc.close(); }
    }, [html]);

    return (
        <div className="email-preview-overlay" onClick={onClose}>
            <div className="email-preview-modal" onClick={e => e.stopPropagation()}>
                <div className="email-preview-modal-header">
                    <div>
                        <h3 style={{ margin: 0, color: 'var(--text)' }}>
                            {step.subject_line || 'Email Preview'}
                        </h3>
                        {step.preview_text && (
                            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                {step.preview_text}
                            </p>
                        )}
                    </div>
                    <GlassButton variant="ghost" onClick={onClose} style={{ padding: '4px 8px' }}>
                        <X size={18} />
                    </GlassButton>
                </div>
                <div className="email-preview-modal-body">
                    {step.step_id ? (
                        <iframe
                            ref={iframeRef}
                            title="Email Preview"
                            sandbox="allow-same-origin"
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                background: '#f9fafb',
                                borderRadius: '0 0 12px 12px',
                            }}
                        />
                    ) : (
                        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
                            No email content to preview.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


interface Props {
    sequence: EmailSequence;
    campaign: Campaign;
    onBack: () => void;
}

interface EditingStep extends Partial<EmailSequenceStep> {
    isNew?: boolean;
}

const STEP_TYPE_OPTIONS = [
    { value: 'email', label: 'Email' },
    { value: 'delay', label: 'Delay' },
    { value: 'condition', label: 'Condition' },
];

const EmailSequenceBuilder: React.FC<Props> = ({ sequence: initialSequence, campaign, onBack }) => {
    const [sequence, setSequence] = useState<EmailSequence>(initialSequence);
    const [generating, setGenerating] = useState(false);
    const [editingStep, setEditingStep] = useState<EditingStep | null>(null);
    const [editorTab, setEditorTab] = useState<'html' | 'plain' | 'preview'>('preview');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [crmSyncing, setCrmSyncing] = useState(false);
    const [crmSynced, setCrmSynced] = useState(!!initialSequence.crm_synced_at);
    const [crmError, setCrmError] = useState('');
    const [previewStep, setPreviewStep] = useState<EmailSequenceStep | null>(null);
    const editorPreviewRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        if (editorTab === 'preview' && editorPreviewRef.current && editingStep?.body_html) {
            const doc = editorPreviewRef.current.contentDocument;
            if (doc) {
                doc.open();
                doc.write(editingStep.body_html);
                doc.close();
            }
        }
    }, [editorTab, editingStep?.body_html]);

    const handleGenerate = async () => {
        setGenerating(true);
        setError('');
        try {
            const updated = await generateSequence(sequence.sequence_id);
            setSequence(updated);
        } catch (err: any) {
            setError(getApiError(err, 'Generation failed.'));
        } finally {
            setGenerating(false);
        }
    };

    const handleSyncToCRM = async () => {
        setCrmSyncing(true);
        setCrmError('');
        try {
            await executeEmailSequence(sequence.sequence_id);
            setCrmSynced(true);
        } catch (err: any) {
            setCrmError(getApiError(err, 'Sync failed.'));
        } finally {
            setCrmSyncing(false);
        }
    };

    const sortedSteps = [...(sequence.steps || [])].sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0));

    // Publication-aligned ("broadcast") sequences derive each step's send time
    // from its article's publication date — delay_days is inert (G6).
    const isBroadcast = (sequence as any).send_mode === 'broadcast';

    const handleEditStep = (step: EmailSequenceStep) => {
        setEditingStep({ ...step, isNew: false });
        setEditorTab('preview');
    };

    const handleAddStep = () => {
        const maxOrder = sortedSteps.reduce((m, s) => Math.max(m, s.step_order ?? 0), 0);
        setEditingStep({
            isNew: true,
            step_order: maxOrder + 1,
            step_type: 'email',
            delay_days: sortedSteps.length === 0 ? 0 : 3,
            subject_line: '',
            body_html: '',
            body_plain: '',
        });
        setEditorTab('html');
    };

    const handleSaveStep = async () => {
        if (!editingStep) return;
        setSaving(true);
        setError('');
        try {
            const payload = {
                sequence_id: sequence.sequence_id,
                step_type: editingStep.step_type || 'email',
                step_order: editingStep.step_order ?? 1,
                delay_days: editingStep.delay_days ?? 0,
                subject_line: editingStep.subject_line || '',
                preview_text: editingStep.preview_text || '',
                body_html: editingStep.body_html || '',
                body_plain: editingStep.body_plain || '',
            };
            if (editingStep.isNew) {
                const created = await createStep(sequence.sequence_id, payload);
                setSequence(prev => ({
                    ...prev,
                    steps: [...(prev.steps || []), created],
                }));
            } else if (editingStep.step_id) {
                const updated = await updateStep(sequence.sequence_id, editingStep.step_id, payload);
                setSequence(prev => ({
                    ...prev,
                    steps: (prev.steps || []).map(s => s.step_id === updated.step_id ? updated : s),
                }));
            }
            setEditingStep(null);
        } catch (err: any) {
            setError(getApiError(err, 'Save failed.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteStep = async (step: EmailSequenceStep) => {
        if (!window.confirm(`Delete step ${step.step_order}?`)) return;
        try {
            await deleteStep(sequence.sequence_id, step.step_id);
            setSequence(prev => ({
                ...prev,
                steps: (prev.steps || []).filter(s => s.step_id !== step.step_id),
            }));
        } catch (err: any) {
            setError(getApiError(err, 'Delete failed.'));
        }
    };

    const handleToggleApproval = async (step: EmailSequenceStep) => {
        // Default to 'approved' when missing so behaviour matches the badge.
        const current = (step.approval_status as any) || 'approved';
        const isApproved = current === 'approved';
        const next: 'approved' | 'pending' = isApproved ? 'pending' : 'approved';
        const label = step.subject_line || `Step ${step.step_order}`;
        const sequencePaused = sequence.status === 'paused';
        const nextBatchAt = (sequence as any).next_send_at as string | undefined;

        let message: string;
        if (isApproved) {
            message =
                `Unapprove "${label}"?\n\n` +
                `Recipients waiting at this step will stay queued. ` +
                `Sending resumes only when you approve the step again.`;
        } else {
            let when: string;
            if (sequencePaused) {
                when = "the sequence's next batch (the sequence is currently paused — resume it to start sending)";
            } else if (nextBatchAt) {
                const dt = new Date(nextBatchAt);
                if (dt <= new Date()) {
                    when = "the sequence's next scheduled batch (already due — sends shortly)";
                } else {
                    const formatted = dt.toLocaleString(undefined, {
                        weekday: 'short', day: '2-digit', month: 'short',
                        hour: '2-digit', minute: '2-digit',
                    });
                    when = `the sequence's next scheduled batch (${formatted})`;
                }
            } else {
                when = "the sequence's next scheduled batch";
            }
            message =
                `Approve "${label}"?\n\n` +
                `On ${when}, this step will send to every recipient currently waiting at it.`;
        }
        if (!window.confirm(message)) return;

        try {
            const updated = await updateStep(sequence.sequence_id, step.step_id, { approval_status: next });
            setSequence(prev => ({
                ...prev,
                steps: (prev.steps || []).map(s => s.step_id === step.step_id ? updated : s),
            }));
        } catch (err: any) {
            setError(getApiError(err, 'Approval toggle failed.'));
        }
    };

    const handleMoveStep = async (step: EmailSequenceStep, dir: 'up' | 'down') => {
        const idx = sortedSteps.findIndex(s => s.step_id === step.step_id);
        const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= sortedSteps.length) return;
        const items = sortedSteps.map((s, i) => {
            let newOrder = s.step_order ?? i + 1;
            if (i === idx) newOrder = sortedSteps[swapIdx].step_order ?? swapIdx + 1;
            if (i === swapIdx) newOrder = sortedSteps[idx].step_order ?? idx + 1;
            return { step_id: s.step_id, step_order: newOrder };
        });
        try {
            const updated = await reorderSteps(sequence.sequence_id, items);
            setSequence(updated);
        } catch (err: any) {
            setError(getApiError(err, 'Reorder failed.'));
        }
    };

    return (
        <div className="sequence-builder">
            <div className="sequence-builder-header">
                <GlassButton variant="ghost" onClick={onBack} style={{ marginRight: 8 }}>
                    <ArrowLeft size={16} />
                </GlassButton>
                <div>
                    <h2 style={{ margin: 0 }}>{sequence.name || 'Email Sequence'}</h2>
                    <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        {campaign.name} &middot; {sortedSteps.length} step{sortedSteps.length !== 1 ? 's' : ''}
                    </p>
                    {isBroadcast && (
                        <span
                            title="Each step sends at a fixed time derived from its article's publication date. Delay (days) is ignored."
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                marginTop: 6, padding: '2px 9px', borderRadius: 9999,
                                fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.03em',
                                background: 'var(--panel-strong)',
                                border: '1px solid var(--stroke)',
                                color: 'var(--text-secondary)',
                            }}
                        >
                            <CalendarClock size={12} /> Publication-aligned — delays not used
                        </span>
                    )}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    {!crmSynced && (
                        <GlassButton
                            variant="secondary"
                            onClick={handleSyncToCRM}
                            disabled={crmSyncing || sortedSteps.length === 0}
                        >
                            {crmSyncing ? 'Syncing…' : <><Share2 size={14} /> Sync to CRM</>}
                        </GlassButton>
                    )}
                    {crmSynced && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#22c55e', fontSize: '0.85rem' }}>
                            <CheckCircle size={14} /> Synced
                        </span>
                    )}
                    <GlassButton
                        variant="primary"
                        onClick={handleGenerate}
                        disabled={generating}
                    >
                        {generating ? 'Generating…' : <><Zap size={14} /> AI Generate</>}
                    </GlassButton>
                </div>
            </div>

            {error && <div className="sequence-error">{error}</div>}
            {crmError && <div className="sequence-error">{crmError}</div>}

            {/* Timeline */}
            {sortedSteps.length === 0 ? (
                <div className="sequence-empty">
                    <Mail size={32} />
                    <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
                        No steps yet. Click <strong>AI Generate</strong> or <strong>Add Step</strong>.
                    </p>
                </div>
            ) : (
                <div className="sequence-timeline">
                    {sortedSteps.map((step, idx) => {
                        const bodySnippet = step.body_html ? stripHtml(step.body_html) : '';
                        return (
                        <div key={step.step_id} className="sequence-step-wrapper">
                            <div className="step-connector">
                                <div className="step-dot">{step.step_order}</div>
                                {idx < sortedSteps.length - 1 && <div className="step-line" />}
                            </div>
                            <GlassCard className="step-card">
                                <div className="step-card-header">
                                    <div className="step-card-meta">
                                        <span className="step-delay-badge">
                                            <Clock size={11} />
                                            Day {step.delay_days}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            {step.step_type}
                                        </span>
                                        <span
                                            className="step-approval-badge"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                padding: '2px 8px',
                                                borderRadius: 9999,
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                background: step.approval_status === 'approved'
                                                    ? 'rgba(34, 197, 94, 0.12)'
                                                    : 'rgba(234, 179, 8, 0.15)',
                                                color: step.approval_status === 'approved'
                                                    ? 'var(--success, #16a34a)'
                                                    : '#a16207',
                                                border: `1px solid ${step.approval_status === 'approved' ? 'rgba(34,197,94,0.35)' : 'rgba(234,179,8,0.4)'}`,
                                            }}
                                            title={step.approval_status === 'approved'
                                                ? 'Step is approved — sends on next due tick'
                                                : 'Step is pending review — send loop will skip it'}
                                        >
                                            {step.approval_status === 'approved' ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />}
                                            {step.approval_status === 'approved' ? 'Approved' : 'Pending'}
                                        </span>
                                    </div>
                                    <div className="step-card-actions">
                                        <GlassButton
                                            variant={step.approval_status === 'approved' ? 'secondary' : 'primary'}
                                            onClick={() => handleToggleApproval(step)}
                                            title={step.approval_status === 'approved' ? 'Unapprove (gate sends)' : 'Approve & queue for send'}
                                            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                        >
                                            {step.approval_status === 'approved' ? 'Unapprove' : 'Approve'}
                                        </GlassButton>
                                        <GlassButton
                                            variant="ghost"
                                            onClick={() => handleMoveStep(step, 'up')}
                                            disabled={idx === 0}
                                            style={{ padding: '4px 8px' }}
                                        >
                                            ↑
                                        </GlassButton>
                                        <GlassButton
                                            variant="ghost"
                                            onClick={() => handleMoveStep(step, 'down')}
                                            disabled={idx === sortedSteps.length - 1}
                                            style={{ padding: '4px 8px' }}
                                        >
                                            ↓
                                        </GlassButton>
                                        {step.body_html && (
                                            <GlassButton variant="ghost" onClick={() => setPreviewStep(step)} title="Preview email">
                                                <Eye size={14} />
                                            </GlassButton>
                                        )}
                                        <GlassButton variant="ghost" onClick={() => handleEditStep(step)}>
                                            <Edit2 size={14} />
                                        </GlassButton>
                                        <GlassButton variant="ghost" onClick={() => handleDeleteStep(step)}>
                                            <Trash2 size={14} />
                                        </GlassButton>
                                    </div>
                                </div>
                                {step.subject_line && (
                                    <p className="step-card-subject">{step.subject_line}</p>
                                )}
                                {step.preview_text && (
                                    <p className="step-card-preview">{step.preview_text}</p>
                                )}
                                {bodySnippet && !step.preview_text && (
                                    <p className="step-card-preview">
                                        {bodySnippet.length > 120 ? bodySnippet.slice(0, 120) + '…' : bodySnippet}
                                    </p>
                                )}
                            </GlassCard>
                        </div>
                        );
                    })}
                </div>
            )}

            {/* Add step CTA */}
            <div className="sequence-add-step" onClick={handleAddStep}>
                <Plus size={16} />
                Add Step
            </div>

            {/* Step editor panel */}
            {editingStep && (
                <div className="step-editor-panel">
                    <div className="step-editor-header">
                        <h3>{editingStep.isNew ? 'New Step' : `Edit Step ${editingStep.step_order}`}</h3>
                        <GlassButton variant="ghost" onClick={() => setEditingStep(null)}>
                            ✕
                        </GlassButton>
                    </div>

                    <div className="step-editor-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Step Type</label>
                                <GlassSelect
                                    value={editingStep.step_type || 'email'}
                                    onChange={val => setEditingStep(prev => prev ? { ...prev, step_type: val } : prev)}
                                    options={STEP_TYPE_OPTIONS}
                                />
                            </div>
                            <div className="form-group">
                                <label>Delay (days)</label>
                                <GlassInput
                                    type="number"
                                    value={String(editingStep.delay_days ?? 0)}
                                    disabled={isBroadcast}
                                    onChange={val => setEditingStep(prev => prev ? { ...prev, delay_days: parseInt(val) || 0 } : prev)}
                                />
                                {isBroadcast && (
                                    <small style={{
                                        display: 'block', marginTop: 4, lineHeight: 1.4,
                                        fontSize: '0.72rem', color: 'var(--text-secondary)',
                                    }}>
                                        Ignored in publication-aligned mode — the send time comes from the
                                        linked article's publication date.
                                    </small>
                                )}
                            </div>
                        </div>

                        {(editingStep.step_type || 'email') === 'email' && (
                            <>
                                <div className="form-group">
                                    <label>Subject Line</label>
                                    <GlassInput
                                        placeholder="Email subject…"
                                        value={editingStep.subject_line || ''}
                                        onChange={val => setEditingStep(prev => prev ? { ...prev, subject_line: val } : prev)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Preview Text</label>
                                    <GlassInput
                                        placeholder="Inbox preview text…"
                                        value={editingStep.preview_text || ''}
                                        onChange={val => setEditingStep(prev => prev ? { ...prev, preview_text: val } : prev)}
                                    />
                                </div>
                                <div>
                                    <div className="step-editor-tabs">
                                        <button
                                            className={`step-editor-tab${editorTab === 'preview' ? ' active' : ''}`}
                                            onClick={() => setEditorTab('preview')}
                                        >
                                            Preview
                                        </button>
                                        <button
                                            className={`step-editor-tab${editorTab === 'html' ? ' active' : ''}`}
                                            onClick={() => setEditorTab('html')}
                                        >
                                            HTML
                                        </button>
                                        <button
                                            className={`step-editor-tab${editorTab === 'plain' ? ' active' : ''}`}
                                            onClick={() => setEditorTab('plain')}
                                        >
                                            Plain Text
                                        </button>
                                    </div>
                                    {editorTab === 'preview' ? (
                                        <div style={{
                                            width: '100%', minHeight: 220,
                                            background: '#f9fafb', border: '1px solid var(--stroke)',
                                            borderRadius: 'var(--radius-md)', overflow: 'hidden',
                                        }}>
                                            {editingStep.body_html ? (
                                                <iframe
                                                    ref={editorPreviewRef}
                                                    title="Editor Preview"
                                                    sandbox="allow-same-origin"
                                                    style={{ width: '100%', minHeight: 220, border: 'none', background: '#f9fafb' }}
                                                />
                                            ) : (
                                                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                                                    No content yet. Switch to the HTML tab to add email content.
                                                </div>
                                            )}
                                        </div>
                                    ) : editorTab === 'html' ? (
                                        <textarea
                                            style={{
                                                width: '100%', minHeight: 220, resize: 'vertical',
                                                background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
                                                borderRadius: 'var(--radius-md)', padding: 10,
                                                color: 'var(--text)', fontFamily: 'monospace', fontSize: '0.82rem',
                                                outline: 'none', boxSizing: 'border-box',
                                            }}
                                            placeholder="<p>Email HTML body…</p>"
                                            value={editingStep.body_html || ''}
                                            onChange={e => setEditingStep(prev => prev ? { ...prev, body_html: e.target.value } : prev)}
                                        />
                                    ) : (
                                        <textarea
                                            style={{
                                                width: '100%', minHeight: 220, resize: 'vertical',
                                                background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
                                                borderRadius: 'var(--radius-md)', padding: 10,
                                                color: 'var(--text)', fontSize: '0.85rem',
                                                outline: 'none', boxSizing: 'border-box',
                                            }}
                                            placeholder="Plain text email body…"
                                            value={editingStep.body_plain || ''}
                                            onChange={e => setEditingStep(prev => prev ? { ...prev, body_plain: e.target.value } : prev)}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="step-editor-footer">
                        <GlassButton variant="secondary" onClick={() => setEditingStep(null)}>
                            Cancel
                        </GlassButton>
                        <GlassButton variant="primary" onClick={handleSaveStep} disabled={saving}>
                            {saving ? 'Saving…' : 'Save Step'}
                        </GlassButton>
                    </div>
                </div>
            )}

            {/* Full-screen preview modal */}
            {previewStep && (
                <EmailPreviewModal step={previewStep} onClose={() => setPreviewStep(null)} />
            )}
        </div>
    );
};

export default EmailSequenceBuilder;
